import { z } from 'zod';
import { BusinessDateSchema, IdSchema } from './encoding.js';
/**
 * Staff onboarding (CLR-48; FRD-HRP-099 to HRP-104): templates of tasks HR maintains, and one
 * checklist per employee started from a template. Each task has a responsible employee and a due
 * date and is ticked done by HR or by that person.
 */
const StatusSchema = z.enum(['active', 'inactive']);
export const OnboardingTemplateTaskSchema = z.object({
    title: z.string(),
    responsibleEmployeeId: IdSchema,
    responsibleName: z.string().nullable(),
    /** Days after the joining date the task is due. */
    dueDays: z.number().int(),
});
export const OnboardingTemplateSchema = z.object({
    id: IdSchema,
    name: z.string(),
    status: StatusSchema,
    tasks: z.array(OnboardingTemplateTaskSchema),
    version: z.number().int(),
});
const TemplateTaskInputSchema = z.strictObject({
    title: z.string().trim().min(1).max(200),
    responsibleEmployeeId: IdSchema,
    dueDays: z.number().int().min(0).max(365),
});
export const CreateOnboardingTemplateRequestSchema = z.strictObject({
    name: z.string().trim().min(1).max(100),
    tasks: z.array(TemplateTaskInputSchema).min(1).max(100),
});
/** `tasks` replaces the list as a whole (`If-Match`). */
export const UpdateOnboardingTemplateRequestSchema = z.strictObject({
    name: z.string().trim().min(1).max(100).optional(),
    tasks: z.array(TemplateTaskInputSchema).min(1).max(100).optional(),
    status: StatusSchema.optional(),
});
export const OnboardingTaskSchema = z.object({
    id: IdSchema,
    title: z.string(),
    responsibleEmployeeId: IdSchema,
    responsibleName: z.string().nullable(),
    dueDate: z.string(),
    done: z.boolean(),
    doneAt: z.string().nullable(),
    /** The user who ticked it, for their photo beside the name. */
    doneBy: IdSchema.nullable(),
    doneByName: z.string().nullable(),
    /** The session may tick it: HR with the change permission, or its responsible employee. */
    canTick: z.boolean(),
    version: z.number().int(),
});
export const OnboardingSchema = z.object({
    id: IdSchema,
    employeeId: IdSchema,
    employeeCode: z.string(),
    employeeName: z.string(),
    templateName: z.string().nullable(),
    status: z.enum(['open', 'completed']),
    startedAt: z.string(),
    completedAt: z.string().nullable(),
    tasksDone: z.number().int(),
    taskCount: z.number().int(),
    tasks: z.array(OnboardingTaskSchema),
    /** The session may add, change and remove tasks (HR). */
    canManage: z.boolean(),
    version: z.number().int(),
});
export const StartOnboardingRequestSchema = z.strictObject({
    templateId: IdSchema,
});
export const AddOnboardingTaskRequestSchema = z.strictObject({
    title: z.string().trim().min(1).max(200),
    responsibleEmployeeId: IdSchema,
    dueDate: BusinessDateSchema,
});
/** Ticks or unticks a task (`If-Match`). */
export const TickOnboardingTaskRequestSchema = z.strictObject({ done: z.boolean() });
/** A task assigned to the session's employee, with whose onboarding it belongs to. */
export const MyOnboardingTaskSchema = OnboardingTaskSchema.extend({
    employeeId: IdSchema,
    employeeCode: z.string(),
    employeeName: z.string(),
});
//# sourceMappingURL=hr-onboarding.js.map